import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Globe, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { api } from '../lib/api/services';
import { toast } from 'react-hot-toast';
import type { CreateCompanyFormData } from '../types';

const CreateCompanyForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState<CreateCompanyFormData>({
    name: '',
    domain: '',
    ownerEmail: '',
    ownerName: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Create the company
      const companyResponse = await api.createCompany({
        name: formData.name,
        domain: formData.domain,
      });

      // Create invite for the company admin
      const inviteResponse = await api.createCompanyInvite({
        email: formData.ownerEmail,
        role: 'CompanyAdmin',
        companyId: companyResponse.company.id,
      });

      toast.success('Company created successfully!');
      toast.success(`Invite sent to ${formData.ownerEmail}`);
      
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create company');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg">
      <div className="p-6 border-b border-cyan-500/30">
        <h2 className="text-xl font-semibold">Create New Company</h2>
        <p className="text-gray-400 mt-1">Add a new company and invite the owner</p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full bg-black/40 border border-cyan-500/30 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Acme Inc."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Company Domain
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
              className="w-full bg-black/40 border border-cyan-500/30 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="acme.com"
              required
            />
          </div>

          <div className="pt-4 border-t border-cyan-500/30">
            <h3 className="text-lg font-medium mb-4">Owner Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Owner Name
                </label>
                <input
                  type="text"
                  value={formData.ownerName}
                  onChange={(e) => setFormData(prev => ({ ...prev, ownerName: e.target.value }))}
                  className="w-full bg-black/40 border border-cyan-500/30 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="John Smith"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Owner Email
                </label>
                <input
                  type="email"
                  value={formData.ownerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, ownerEmail: e.target.value }))}
                  className="w-full bg-black/40 border border-cyan-500/30 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="john@acme.com"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 pt-4 border-t border-cyan-500/30">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Create & Send Invite
          </Button>
        </div>
      </form>
    </Card>
  );
};

export const Companies: React.FC = () => {
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  
  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: api.getCompanies,
  });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Companies</h1>
          <p className="text-gray-400 mt-2">Manage your organizations</p>
        </div>
        <Button onClick={() => setIsCreateFormOpen(true)}>
          <Plus className="w-5 h-5 mr-2" />
          Add Company
        </Button>
      </div>

      {companies.length === 0 ? (
        <Card className="py-12">
          <div className="text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium">No companies yet</h3>
            <p className="text-gray-400 mt-1">Create your first company to get started</p>
            <Button
              onClick={() => setIsCreateFormOpen(true)}
              className="mt-4"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Company
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company) => (
            <Card key={company.id} className="hover:border-cyan-400/50 transition-colors">
              <div className="flex items-start gap-4">
                {company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-cyan-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{company.name}</h3>
                  <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                    <Globe className="w-4 h-4" />
                    <span className="truncate">{company.domain}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-cyan-500/30 text-sm text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>
                  Created {new Date(company.created_at).toLocaleDateString()}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {isCreateFormOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm">
          <CreateCompanyForm onClose={() => setIsCreateFormOpen(false)} />
        </div>
      )}
    </div>
  );
};